export type DownReasonEntry = { reason: string; date: string };

export type DownReasonPieSlice = { label: string; value: number; color: string };

const DOWN_REASON_COLORS = {
    preventable: '#ff9900',
    normalWear: '#879596',
    service: '#0073bb',
} as const;

export const parseDownReasonEntries = (raw: string | null | undefined): DownReasonEntry[] | null => {
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

export const getDownReasonPieSlices = (
    entries: DownReasonEntry[],
): { slices: DownReasonPieSlice[]; total: number } => {
    const summaryCounts = entries.reduce(
        (acc, item) => {
            switch (item.reason) {
                case 'Preventable':
                    acc.preventable++;
                    break;
                case 'Normal Wear and Tear':
                    acc.normalWear++;
                    break;
                case 'Service Item':
                    acc.service++;
                    break;
            }
            return acc;
        },
        { preventable: 0, normalWear: 0, service: 0 },
    );

    const slices = [
        { label: 'Preventable', value: summaryCounts.preventable, color: DOWN_REASON_COLORS.preventable },
        { label: 'Normal Wear & Tear', value: summaryCounts.normalWear, color: DOWN_REASON_COLORS.normalWear },
        { label: 'Service Item', value: summaryCounts.service, color: DOWN_REASON_COLORS.service },
    ].filter((item) => item.value > 0);

    const total = slices.reduce((sum, item) => sum + item.value, 0);

    return { slices, total };
};

export const hasDownReasonChartData = (raw: string | null | undefined): boolean => {
    const entries = parseDownReasonEntries(raw);
    if (!entries || entries.length === 0) return false;
    return getDownReasonPieSlices(entries).total > 0;
};

export const buildDownReasonPieSlicePath = (
    value: number,
    total: number,
    startAngle: number,
    radius = 80,
    centerX = 100,
    centerY = 100,
) => {
    const sliceAngle = (value / total) * 360;
    const endAngle = startAngle + sliceAngle;

    const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
    const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
    const x2 = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
    const y2 = centerY + radius * Math.sin((endAngle * Math.PI) / 180);
    const largeArc = sliceAngle > 180 ? 1 : 0;

    return {
        d: [`M ${centerX} ${centerY}`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`, 'Z'].join(
            ' ',
        ),
        endAngle,
    };
};
